import { ReactNode } from "react";
import { Button, Flex, InfoBox, ModalV2, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Sizes } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";

export type ProgramChangeRow = {
  label: string;
  current: string;
  next: string;
  /** Marks the current value with a warning triangle (e.g. Legacy KYC). */
  currentHasWarning?: boolean;
};

export type ChangeProgramsModalProps = {
  show: boolean;
  title: string;
  description: ReactNode;
  /** Changed rows only, rendered as a Current | New diff table. */
  changes: ProgramChangeRow[];
  /** Warning surfaced above the diff (e.g. Legacy KYC irreversibility). */
  warning?: string;
  applying?: boolean;
  onCancel: () => void;
  onApply: () => void;
};

export const ChangeProgramsModal = ({
  show,
  title,
  description,
  changes,
  warning,
  applying,
  onCancel,
  onApply,
}: ChangeProgramsModalProps) => (
  // While applying, backdrop/Esc dismissal is blocked along with the Cancel button
  <ModalV2
    show={show}
    onHide={applying ? () => undefined : onCancel}
    isCentered
    size="xl"
    title={title}
  >
    <Flex flexDirection="column" gap={Sizes.MD}>
      <Text>{description}</Text>
      {warning && <InfoBox variant="warning" title={warning} />}
      <div className={styles.diffTable}>
        <div className={styles.diffHeaderRow}>
          <div className={styles.diffLabelCell} />
          <div className={styles.diffHeaderCell}>
            <Text variant="strong-md">Current</Text>
          </div>
          <div className={styles.diffHeaderCell}>
            <Text variant="strong-md">New</Text>
          </div>
        </div>
        {changes.map((change) => (
          <div key={change.label} className={styles.diffRow}>
            <div className={styles.diffLabelCell}>
              <Text variant="strong-md">{change.label}</Text>
            </div>
            <div className={styles.diffValueCell}>
              {change.currentHasWarning && (
                <Icon type="exclamation-triangle" className={styles.diffWarningIcon} />
              )}
              <Text>{change.current}</Text>
            </div>
            <div className={styles.diffValueCell}>
              <Text>{change.next}</Text>
            </div>
          </div>
        ))}
      </div>
      <Text>
        This change will only apply to new cases. The in-progress cases will remain unchanged.
      </Text>
      <Flex justifyContent="flex-end" gap={12}>
        <Button variant="neutral" onClick={onCancel} disabled={applying}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onApply}
          isLoading={applying}
          data-testid="apply-program-changes-button"
        >
          Apply changes
        </Button>
      </Flex>
    </Flex>
  </ModalV2>
);
